"use client"

import Script from "next/script"
import { useEffect, useState } from "react"
import { hasConsent } from "@/lib/cookies/consent"
import { markAnalyticsReady } from "@/lib/analytics/track"

// ---------------------------------------------------------------------------
// GoogleAnalytics — consent-gated GA4 (gtag.js) with Consent Mode v2.
//
// Behaviour:
//  - No-op unless NEXT_PUBLIC_GA_MEASUREMENT_ID is set (per-customer ENV).
//  - Consent Mode v2 default is set BEFORE the library config and is `denied`
//    for a fresh visitor (GDPR). The gtag.js library itself is NOT requested
//    until the visitor grants the `analytics` cookie-consent category — so a
//    rejecting visitor causes zero requests to googletagmanager.com.
//  - Reacts to the banner's `cookie-consent-changed` event WITHOUT a reload:
//    grants load the library + push a Consent Mode `update`; withdrawals push
//    an `update` to `denied` (an already-loaded gtag.js cannot be unloaded, so
//    consent withdrawal is expressed via Consent Mode, not by removing the tag).
//  - The three Google ads signals (ad_storage, ad_user_data, ad_personalization)
//    follow the `marketing` category. They matter once the GA4 property is
//    linked to Google Ads: without them Ads only gets modelled conversions.
//  - After gtag('config') it marks GA ready for src/lib/analytics/track.ts,
//    which sends the ecommerce events (view_item, add_to_cart, begin_checkout,
//    purchase) and holds any raised before gtag.js finished loading.
//
// The script tags only render client-side (after `mounted`): consent lives in
// document.cookie, which a server render cannot read, so an SSR render would
// guess wrong for returning visitors and cause a hydration mismatch (React #418
// — a recurring failure mode in this codebase). Keeping the server output empty
// and the first client render identical to it avoids that entirely.
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const BOOTSTRAP_ID = "ga-consent-bootstrap"
const LIB_ID = "ga-base"
const CONSENT_CHANGED_EVENT = "cookie-consent-changed"

export default function GoogleAnalytics() {
  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID

  // `mounted` gates client-only rendering (hydration safety, see header note);
  // `gtagRequested` latches true the first time analytics consent is granted and
  // never flips back (an already-loaded gtag.js can't be unloaded — withdrawal is
  // expressed via a Consent Mode `update`, below). Both live in one state object
  // so the initial client sync is a single setState.
  const [state, setState] = useState<{ mounted: boolean; gtagRequested: boolean }>({
    mounted: false,
    gtagRequested: false,
  })

  useEffect(() => {
    if (!measurementId) return

    // One-shot client mount + initial consent read. Consent lives in
    // document.cookie (unreadable during SSR), so this MUST happen after mount —
    // an intentional, single synchronous state sync, not a cascading-render bug.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState({ mounted: true, gtagRequested: hasConsent("analytics") })

    const handleConsentChange = () => {
      const analyticsGranted = hasConsent("analytics")
      // Live Consent Mode v2 update — queues on dataLayer and is consumed in
      // order once gtag.js runs, so it applies whether or not the lib is loaded.
      const adSignal = hasConsent("marketing") ? "granted" : "denied"
      window.gtag?.("consent", "update", {
        analytics_storage: analyticsGranted ? "granted" : "denied",
        ad_storage: adSignal,
        ad_user_data: adSignal,
        ad_personalization: adSignal,
      })
      if (analyticsGranted) setState((prev) => ({ ...prev, gtagRequested: true }))
    }

    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChange)
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChange)
  }, [measurementId])

  const { mounted, gtagRequested } = state
  if (!measurementId || !mounted) return null

  const analyticsDefault = hasConsent("analytics") ? "granted" : "denied"
  const adDefault = hasConsent("marketing") ? "granted" : "denied"

  // Consent Mode v2 defaults MUST be set before any gtag config runs. We do NOT
  // call gtag('config') here — it is emitted from the library's onLoad below so
  // it runs AFTER any queued consent `update`, keeping the very first page_view
  // on the correct (granted) consent state when a visitor accepts without a reload.
  const bootstrap =
    `window.dataLayer=window.dataLayer||[];` +
    `function gtag(){dataLayer.push(arguments);}` +
    `window.gtag=gtag;` +
    `gtag('consent','default',{` +
    `'analytics_storage':'${analyticsDefault}',` +
    `'ad_storage':'${adDefault}',` +
    `'ad_user_data':'${adDefault}',` +
    `'ad_personalization':'${adDefault}'` +
    `});` +
    `gtag('js',new Date());`

  return (
    <>
      <Script
        id={BOOTSTRAP_ID}
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: bootstrap }}
      />
      {gtagRequested && (
        <Script
          id={LIB_ID}
          src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
          strategy="afterInteractive"
          onLoad={() => {
            window.gtag?.("config", measurementId)
            markAnalyticsReady("ga")
          }}
        />
      )}
    </>
  )
}
